import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Select,
  Checkbox,
  SimpleGrid,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  Badge,
  Switch,
  Spinner,
} from "@chakra-ui/react";
import { MdEdit, MdDelete, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import CreateBotChannel from "./CreateBotChannel";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";
import { getTranslatedField } from "utils/translations";

const BOT_CHANNELS_PAGE_ID = PAGE_IDS.BOT_CHANNELS;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const PLATFORM_COLORS = { TELEGRAM: "blue", WHATSAPP: "green" };

const ListBotChannel = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "md" });

  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editableChannel, setEditableChannel] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderColor = useColorModeValue("gray.600", "gray.200");
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) return;
      const permission = role.permissions.find((p) => p.page_id === BOT_CHANNELS_PAGE_ID);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      // ignore malformed permissions blob
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/category`);
      setCategories(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/bot/target?page_id=${BOT_CHANNELS_PAGE_ID}`);
      setChannels(response?.data || []);
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to fetch channels."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    fetchCategories();
    fetchChannels();
  }, [fetchCategories, fetchChannels]);

  const handleCreated = () => {
    setShowCreateModal(false);
    fetchChannels();
  };

  const handleEdit = (channel) => {
    setEditableChannel({
      ...channel,
      selectedCategoryIds: (channel.categories || []).map((c) => c.id),
    });
  };

  const toggleEditCategory = (categoryId) => {
    setEditableChannel((prev) => ({
      ...prev,
      selectedCategoryIds: prev.selectedCategoryIds.includes(categoryId)
        ? prev.selectedCategoryIds.filter((id) => id !== categoryId)
        : [...prev.selectedCategoryIds, categoryId],
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const data = {
        page_id: BOT_CHANNELS_PAGE_ID,
        targetId: editableChannel.id,
        platform: editableChannel.platform,
        targetType: editableChannel.targetType,
        name: editableChannel.name.trim(),
        externalId: editableChannel.externalId.trim(),
        isActive: editableChannel.isActive,
        breakingOnly: editableChannel.breakingOnly,
        categoryIds: editableChannel.selectedCategoryIds,
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/bot/target`,
        "PUT",
        data
      );
      if (!response) {
        throw new Error("Failed to update channel");
      }
      await fetchChannels();
      setEditableChannel(null);
      toast({
        title: t("Channel updated successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to update channel."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (targetId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/bot/target`,
        "DELETE",
        { page_id: BOT_CHANNELS_PAGE_ID, targetId }
      );
      if (!response) {
        throw new Error("Failed to delete channel");
      }
      setChannels((prev) => prev.filter((c) => c.id !== targetId));
      toast({
        title: t("Channel deleted successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to delete channel."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  // Quick one-click on/off toggle right in the table, same pattern used by
  // Languages/SubCategory — separate from the full edit modal.
  const handleToggleActive = async (channel) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/bot/target`,
        "PUT",
        { page_id: BOT_CHANNELS_PAGE_ID, targetId: channel.id, isActive: !channel.isActive }
      );
      if (!response) {
        throw new Error("Failed to update channel");
      }
      setChannels((prev) => prev.map((c) => (c.id === channel.id ? { ...c, isActive: !c.isActive } : c)));
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to toggle channel status."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const filteredChannels = channels.filter((c) =>
    (c.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.externalId || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }} boxShadow="lg">
      <Flex px={{ base: "10px", md: "25px" }} justify="space-between" mb="20px" align="center" flexWrap="wrap">
        <Flex align="center">
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("Bot Channels")}
          </Text>
          <Button
            onClick={() => setShowCreateModal(true)}
            isDisabled={!permissions.canInsert}
            mb={1}
            ml={4}
            bg={GOLD_BORDER}
            color="#1A1A1A"
            _hover={{ bg: GOLD_TEXT, color: "white" }}
            _active={{ bg: GOLD_TEXT, color: "white" }}
            leftIcon={<MdAdd />}
            borderRadius="full"
            boxShadow="sm"
          >
            {t("Add Channel")}
          </Button>
        </Flex>
        <Input
          placeholder={t("Search by name or external id")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="sm"
          w={{ base: "100%", md: "240px" }}
          borderColor={GOLD_BORDER}
          focusBorderColor={GOLD_BORDER}
        />
      </Flex>

      <Box overflowX="auto" overflowY="auto" maxH="60vh" px="0px">
        {loading ? (
          <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr bg={tableHeaderBg}>
                <Th color={tableHeaderColor} fontWeight="600">{t("Name")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Platform")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Type")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("External ID")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Categories")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Breaking Only")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Status")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredChannels.map((channel) => (
                <Tr key={channel.id} borderBottom="1px solid" borderColor="gray.100" _hover={{ bg: rowHoverBg }}>
                  <Td>{channel.name}</Td>
                  <Td>
                    <Badge colorScheme={PLATFORM_COLORS[channel.platform] || "gray"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                      {t(channel.platform)}
                    </Badge>
                  </Td>
                  <Td>{t(channel.targetType)}</Td>
                  <Td maxW="180px" isTruncated>{channel.externalId}</Td>
                  <Td>
                    {(channel.categories || []).length === 0 ? (
                      <Text fontSize="xs" color="gray.500">{t("All")}</Text>
                    ) : (
                      <Wrap>
                        {channel.categories.map((cat) => (
                          <WrapItem key={cat.id}>
                            <Badge colorScheme="gray" variant="subtle" fontSize="0.7em">{cat.slug}</Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={channel.breakingOnly ? "orange" : "gray"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                      {channel.breakingOnly ? t("Breaking Only") : t("All News")}
                    </Badge>
                  </Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Badge colorScheme={channel.isActive ? "green" : "gray"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                        {channel.isActive ? t("Active") : t("Inactive")}
                      </Badge>
                      <Switch
                        size="sm"
                        isChecked={channel.isActive}
                        isDisabled={!permissions.canEdit}
                        onChange={() => handleToggleActive(channel)}
                        colorScheme="brand"
                      />
                    </Flex>
                  </Td>
                  <Td>
                    <Flex>
                      <Button
                        variant="outline"
                        borderColor={GOLD_BORDER}
                        color={GOLD_TEXT}
                        _hover={{ bg: GOLD_LIGHT }}
                        size="sm"
                        mr="2"
                        isDisabled={!permissions.canEdit}
                        onClick={() => handleEdit(channel)}
                        leftIcon={<MdEdit />}
                      >
                        {t("Edit")}
                      </Button>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        isDisabled={!permissions.canDelete}
                        onClick={() => handleDelete(channel.id)}
                        leftIcon={<MdDelete />}
                      >
                        {t("Delete")}
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {editableChannel && (
        <Modal isOpen={true} onClose={() => setEditableChannel(null)} size={modalSize}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader borderBottom="1px solid" borderColor="gray.100">
              <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
                {t("Edit Channel")}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Platform")}</Text>
                  <Select value={editableChannel.platform} onChange={(e) => setEditableChannel({ ...editableChannel, platform: e.target.value })} {...fieldProps}>
                    <option value="TELEGRAM">{t("Telegram")}</option>
                    <option value="WHATSAPP">{t("WhatsApp")}</option>
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Type")}</Text>
                  <Select value={editableChannel.targetType} onChange={(e) => setEditableChannel({ ...editableChannel, targetType: e.target.value })} {...fieldProps}>
                    <option value="CHANNEL">{t("Channel")}</option>
                    <option value="GROUP">{t("Group")}</option>
                  </Select>
                </Box>
              </SimpleGrid>

              <Box mt="16px">
                <Text fontSize="sm" color="gray.600" mb="2">{t("Name")}</Text>
                <Input
                  value={editableChannel.name}
                  onChange={(e) => setEditableChannel({ ...editableChannel, name: e.target.value })}
                  {...fieldProps}
                />
              </Box>

              <Box mt="16px">
                <Text fontSize="sm" color="gray.600" mb="2">{t("External ID")}</Text>
                <Input
                  value={editableChannel.externalId}
                  onChange={(e) => setEditableChannel({ ...editableChannel, externalId: e.target.value })}
                  {...fieldProps}
                />
              </Box>

              <Box mt="16px">
                <Text fontSize="sm" color="gray.600" mb="2">{t("Categories")}</Text>
                {categories.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">{t("No categories yet.")}</Text>
                ) : (
                  <Wrap>
                    {categories.map((category) => {
                      const isSelected = editableChannel.selectedCategoryIds.includes(category.id);
                      return (
                        <WrapItem key={category.id}>
                          <Tag
                            as="button"
                            type="button"
                            onClick={() => toggleEditCategory(category.id)}
                            bg={isSelected ? GOLD_BORDER : "gray.100"}
                            color={isSelected ? "#1A1A1A" : "gray.700"}
                            cursor="pointer"
                            borderRadius="full"
                          >
                            <TagLabel>{getTranslatedField(category.translations, "en")}</TagLabel>
                          </Tag>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                )}
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
                <Checkbox
                  isChecked={editableChannel.isActive}
                  onChange={(e) => setEditableChannel({ ...editableChannel, isActive: e.target.checked })}
                  colorScheme="brand"
                >
                  {t("Active")}
                </Checkbox>
                <Checkbox
                  isChecked={editableChannel.breakingOnly}
                  onChange={(e) => setEditableChannel({ ...editableChannel, breakingOnly: e.target.checked })}
                  colorScheme="brand"
                >
                  {t("Breaking news only")}
                </Checkbox>
              </SimpleGrid>
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
              <Button variant="outline" borderColor="gray.200" color="gray.700" onClick={() => setEditableChannel(null)}>
                {t("Cancel")}
              </Button>
              <Button
                bg={GOLD_BORDER}
                color="#1A1A1A"
                _hover={{ bg: GOLD_TEXT, color: "white" }}
                _active={{ bg: GOLD_TEXT, color: "white" }}
                onClick={handleSave}
                isLoading={isSaving}
              >
                {t("Save")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size={modalSize}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
              {t("Create Channel")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateBotChannel onCreated={handleCreated} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default ListBotChannel;
